import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import SearchDialog from "./content/SearchDialog";

export default function InPageSearch({ containerId, panelView = false }) {
  const [searchState, setSearchState] = useState({
    isSearchOpen: false,
    searchText: "",
    matches: [],
    currentIndex: -1,
  });

  // Highlight text in the DOM
  const highlightText = useCallback(
    (text) => {
      if (text.length < 3) {
        // Clear highlights if text is too short
        const container = document.getElementById(containerId);
        if (container) {
          const highlights = container.querySelectorAll(".search-highlight");
          highlights.forEach((el) => {
            const parent = el.parentNode;
            while (el.firstChild) {
              parent.insertBefore(el.firstChild, el);
            }
            parent.removeChild(el);
          });
          container.normalize();
        }
        return [];
      }
      const container = document.getElementById(containerId);
      if (!container || !text) {
        // Clear highlights if no text
        const highlights = container
          ? container.querySelectorAll(".search-highlight")
          : [];
        highlights.forEach((el) => {
          const parent = el.parentNode;
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
        });
        if (container) container.normalize();
        return [];
      }

      // First, remove existing highlights by restoring original text
      const existingHighlights = container.querySelectorAll(".search-highlight");
      existingHighlights.forEach((el) => {
        const parent = el.parentNode;
        while (el.firstChild) {
          parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
      });
      // Merge adjacent text nodes that were split by the previous highlight pass,
      // otherwise a search for "Firmen" won't match across the split nodes "Firm" + "eninfo".
      container.normalize();

      // Find all text nodes in the container
      const textNodes = [];
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      let node;
      while ((node = walker.nextNode())) {
        // Skip text nodes inside mark elements to avoid processing already-highlighted text
        if (node.parentNode && node.parentNode.tagName === "MARK") {
          continue;
        }
        // Skip text nodes inside dropdown content
        if (node.parentNode && node.parentNode.closest(".w3-dropdown-content")) {
          continue;
        }
        textNodes.push(node);
      }

      const matches = [];
      const regex = new RegExp(
        `(${text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
        "gi"
      );

      textNodes.forEach((textNode) => {
        const textContent = textNode.textContent;
        regex.lastIndex = 0; // Reset BEFORE test to prevent lastIndex from affecting results
        if (regex.test(textContent)) {
          const fragment = document.createDocumentFragment();
          let lastIndex = 0;
          let match;

          regex.lastIndex = 0;
          while ((match = regex.exec(textContent)) !== null) {
            // Add text before match
            if (match.index > lastIndex) {
              fragment.appendChild(
                document.createTextNode(
                  textContent.slice(lastIndex, match.index)
                )
              );
            }
            // Add highlighted match
            const mark = document.createElement("mark");
            mark.className = "search-highlight";
            mark.textContent = match[0];
            fragment.appendChild(mark);
            matches.push(mark);
            lastIndex = regex.lastIndex;
          }

          // Add remaining text
          if (lastIndex < textContent.length) {
            fragment.appendChild(
              document.createTextNode(textContent.slice(lastIndex))
            );
          }

          textNode.parentNode.replaceChild(fragment, textNode);
        }
        regex.lastIndex = 0; // Reset regex
      });

      return matches;
    },
    [containerId]
  );

  // Scroll to a specific match
  const scrollToMatch = useCallback((matchIndex, matches) => {
    if (matches.length === 0) return;

    // Remove 'current' class from all matches
    matches.forEach((m) => m.classList.remove("search-highlight-current"));

    const match = matches[matchIndex];
    if (match) {
      match.classList.add("search-highlight-current");
      match.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  // Handle search
  const handleSearch = useCallback(
    (text) => {
      // Function to attempt highlighting with retries
      const tryHighlight = (attempts = 0) => {
        const container = document.getElementById(containerId);

        if (!container) {
          if (attempts < 10) {
            // Retry after a delay
            setTimeout(() => tryHighlight(attempts + 1), 200);
            return;
          }
          setSearchState((prev) => ({
            ...prev,
            searchText: text,
            matches: [],
            currentIndex: -1,
            isSearchOpen: true,
          }));
          return;
        }

        const matches = highlightText(text);
        setSearchState((prev) => ({
          ...prev,
          searchText: text,
          matches: matches,
          currentIndex: matches.length > 0 ? 0 : -1,
          isSearchOpen: true,
        }));

        if (matches.length > 0) {
          setTimeout(() => scrollToMatch(0, matches), 100);
        }
      };

      tryHighlight();
    },
    [containerId, highlightText, scrollToMatch]
  );

  // Navigate to next match
  const handleNext = useCallback(() => {
    const { matches, currentIndex } = searchState;
    if (matches.length === 0) return;

    const nextIndex = (currentIndex + 1) % matches.length;
    setSearchState((prev) => ({ ...prev, currentIndex: nextIndex }));
    scrollToMatch(nextIndex, matches);
  }, [searchState, scrollToMatch]);

  // Navigate to previous match
  const handlePrevious = useCallback(() => {
    const { matches, currentIndex } = searchState;
    if (matches.length === 0) return;

    const prevIndex =
      currentIndex === 0 ? matches.length - 1 : currentIndex - 1;
    setSearchState((prev) => ({ ...prev, currentIndex: prevIndex }));
    scrollToMatch(prevIndex, matches);
  }, [searchState, scrollToMatch]);

  // Navigate to a specific match by index
  const handleSelectMatch = useCallback(
    (matchIndex) => {
      const { matches } = searchState;
      if (matches.length === 0 || matchIndex < 0 || matchIndex >= matches.length) {
        return;
      }

      setSearchState((prev) => ({ ...prev, currentIndex: matchIndex }));
      scrollToMatch(matchIndex, matches);
    },
    [searchState, scrollToMatch]
  );

  // Close search dialog
  const handleClose = useCallback(() => {
    highlightText(""); // Clear highlights
    setSearchState({
      isSearchOpen: false,
      searchText: "",
      matches: [],
      currentIndex: -1,
    });
  }, [highlightText]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+F or Cmd+F to open search
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setSearchState((prev) => ({ ...prev, isSearchOpen: true }));
      }
      // Escape to close search
      if (e.key === "Escape" && searchState.isSearchOpen) {
        handleClose();
      }
      // F3 or Enter with Ctrl to navigate
      if (searchState.isSearchOpen && searchState.matches.length > 0) {
        if (e.key === "F3" || ((e.ctrlKey || e.metaKey) && e.key === "g")) {
          e.preventDefault();
          if (e.shiftKey) {
            handlePrevious();
          } else {
            handleNext();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchState.isSearchOpen, searchState.matches.length, handleClose, handleNext, handlePrevious]);

  // Check for search query in URL on mount
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const urlSearchText = query.get("q");
    if (urlSearchText !== null && urlSearchText !== "") {
      setTimeout(() => {
        handleSearch(urlSearchText);
      }, 100);
    }

    return () => {
      highlightText(""); // Clean up highlights on unmount
    };
  }, [handleSearch, highlightText]);

  if (!searchState.isSearchOpen) {
    return null;
  }

  // Extract context text around each match for better identification
  const getMatchContext = (matchEl) => {
    const maxContextChars = 60;
    const matchText = (matchEl.textContent || "").trim();
    if (!matchText) return `Treffer ${matchEl.dataset.matchIndex || 0 + 1}`;

    // Get the parent container with text content
    const container = document.getElementById(containerId);
    if (!container) return matchText;

    // Get all text content before the match element
    const preceedingText = [];
    const followingText = [];
    
    // Walk through siblings to get context
    let prevSibling = matchEl.previousSibling;
    let charCount = 0;
    
    // Collect text before the match (up to ~20 chars)
    while (prevSibling && charCount < 20) {
      if (prevSibling.nodeType === Node.TEXT_NODE) {
        const text = prevSibling.textContent;
        if (charCount + text.length > 20) {
          preceedingText.unshift("..." + text.slice(-(20 - charCount)));
          charCount = 20;
        } else {
          preceedingText.unshift(text);
          charCount += text.length;
        }
      } else if (prevSibling.nodeType === Node.ELEMENT_NODE && prevSibling.tagName !== 'MARK') {
        const text = prevSibling.textContent || "";
        if (charCount + text.length > 20) {
          preceedingText.unshift("..." + text.slice(-(20 - charCount)));
          charCount = 20;
        } else {
          preceedingText.unshift(text);
          charCount += text.length;
        }
      }
      prevSibling = prevSibling.previousSibling;
    }

    // Collect text after the match (up to ~20 chars)
    charCount = 0;
    let nextSibling = matchEl.nextSibling;
    while (nextSibling && charCount < 20) {
      if (nextSibling.nodeType === Node.TEXT_NODE) {
        const text = nextSibling.textContent;
        if (charCount + text.length > 20) {
          followingText.push(text.slice(0, 20 - charCount) + "...");
          charCount = 20;
        } else {
          followingText.push(text);
          charCount += text.length;
        }
      } else if (nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName !== 'MARK') {
        const text = nextSibling.textContent || "";
        if (charCount + text.length > 20) {
          followingText.push(text.slice(0, 20 - charCount) + "...");
          charCount = 20;
        } else {
          followingText.push(text);
          charCount += text.length;
        }
      }
      nextSibling = nextSibling.nextSibling;
    }

    const preText = preceedingText.join("").trim();
    const postText = followingText.join("").trim();
    
    // Build context string
    let contextText = "";
    if (preText) contextText += preText + " ";
    contextText += matchText;
    if (postText) contextText += " " + postText;
    
    // Truncate if too long
    if (contextText.length > maxContextChars) {
      if (contextText.indexOf(matchText) < maxContextChars / 2) {
        contextText = contextText.slice(0, maxContextChars - 3) + "...";
      } else {
        contextText = "..." + contextText.slice(-(maxContextChars - 3));
      }
    }
    
    return contextText;
  };

  const matchItems = searchState.matches.map((match, index) => ({
    id: index,
    text: getMatchContext(match),
    matchText: (match.textContent || "").trim(),
  }));

  return (
    <SearchDialog
      searchText={searchState.searchText}
      matchCount={searchState.matches.length}
      currentIndex={searchState.currentIndex}
      matches={matchItems}
      onSearch={handleSearch}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onSelectMatch={handleSelectMatch}
      onClose={handleClose}
      panelView={panelView}
    />
  );
}

InPageSearch.propTypes = {
  containerId: PropTypes.string.isRequired,
  panelView: PropTypes.bool,
};